import {useEffect, useRef, useState} from 'react'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import Swal from 'sweetalert2'
import {CircularProgress} from '@mui/material'
import DatePicker from 'react-datepicker'
import {Edit_Tenant, get_Subdivions} from '../Tenant_API'
import MuiPhoneNumber from 'material-ui-phone-number'
import {fontWeight} from '@mui/system'
import '../tenant.css'

const TenantOverview = ({tenantDetails, id, get_view_tenant, dateSuperScript}: any) => {
  const formRef = useRef(null)
  const [show, setShow] = useState<boolean>(false)
  const [editTenantLoading, setEditTenantLoading] = useState<boolean>(false)
  const [agreementDate, setAgreementDate] = useState(null)
  const [tenancyStartDate, setTenancyStartDate] = useState(null)
  const [tenancyEndDate, setTenancyEndDate] = useState(null)
  const [rentalStartDate, setRentalStartDate] = useState(null)
  const [rentalEndDate, setRentalEndDate] = useState(null)
  const [subDivisions, setSubDivisions] = useState([])

  async function checkSubDiv() {
    if (tenantDetails.sub_unit_available) {
      try {
        const {data: res} = await get_Subdivions(tenantDetails.property_uuid)
        if (res.code === '200') {
          setSubDivisions(res.data)
        } else if (res.code === '204') {
          setSubDivisions(res.data)
        } else {
          Swal.fire({
            position: 'center',
            icon: 'error',
            title: res.msg.charAt(0).toUpperCase() + res.msg.slice(1),
            text: res.msg_details,
            allowOutsideClick: true,
          })
        }
      } catch (error: any) {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: error,
        })
      }
    }
  }

  useEffect(() => {
    if (tenantDetails) {
      checkSubDiv()
      if (tenantDetails.rental_agreement_date !== '') {
        // @ts-ignore
        setAgreementDate(new Date(tenantDetails?.rental_agreement_date))
      }
      if (tenantDetails.tenancy_commencement_date !== '') {
        // @ts-ignore
        setTenancyStartDate(new Date(tenantDetails?.tenancy_commencement_date))
      }
      if (tenantDetails.tenancy_expiration_date !== '') {
        // @ts-ignore
        setTenancyEndDate(new Date(tenantDetails?.tenancy_expiration_date))
      }
      if (tenantDetails.property_rent_start_date !== '') {
        // @ts-ignore
        setRentalStartDate(new Date(tenantDetails?.property_rent_start_date))
      }
      if (tenantDetails.property_rent_end_date !== '') {
        // @ts-ignore
        setRentalEndDate(new Date(tenantDetails?.property_rent_end_date))
      }
    }
  }, [tenantDetails])

  const validationSchema = Yup.object({
    business_name: Yup.string().when([], {
      is: () => tenantDetails.property_usage_type === 2,
      then: Yup.string().required('Business name is required'),
      otherwise: Yup.string().notRequired(),
    }),
    business_email_id: Yup.string().when([], {
      is: () => tenantDetails.property_usage_type === 2,
      then: Yup.string().required('Business email address is required'),
      otherwise: Yup.string().notRequired(),
    }),
    business_contact_number: Yup.string().when([], {
      is: () => tenantDetails.property_usage_type === 2,
      then: Yup.string().required('Business contact number is required'),
      otherwise: Yup.string().notRequired(),
    }),
    property_sub_unit_uuid: Yup.string().when([], {
      is: () => tenantDetails?.sub_unit_available,
      then: Yup.string().required('Property subdivision is required'),
      otherwise: Yup.string().notRequired(),
    }),
    property_rent_rate: Yup.number()
      .typeError('Rent amount must be a number')
      .integer('Rent amount must be an integer')
      .positive('Rent amount must be a positive number')
      .min(0, 'Rent amount must be greater than or equal to 0')
      .max(999999999999999, 'Rent amount must have at most 15 digits')
      .required('Rent amount is required'),
    security_deposit: Yup.number()
      .typeError('Security deposit must be a number')
      .integer('Security deposit must be an integer')
      .positive('Security deposit must be a positive number')
      .min(0, 'Security deposit must be greater than or equal to 0')
      .optional(),
    utilities_deposit: Yup.number()
      .typeError('Utilities deposit must be a number')
      .integer('Utilities deposit must be an integer')
      .positive('Utilities deposit must be a positive number')
      .min(0, 'Utilities deposit must be greater than or equal to 0')
      .optional(),
    other_deposits: Yup.number()
      .typeError('Other deposits must be a number')
      .integer('Other deposits must be an integer')
      .positive('Other deposits must be a positive number')
      .min(0, 'Other deposits must be greater than or equal to 0')
      .optional(),
    property_rent_due_date: Yup.string().required('Rent due date is required'),
    tenancy_term: Yup.number()
      .typeError('Tenancy term must be a number')
      .integer('Tenancy term must be an integer')
      .positive('Tenancy term must be a positive number')
      .min(0, 'Tenancy term must be greater than or equal to 0')
      .optional(),
    rental_agreement_date: Yup.date().required('Rental agreement date is required'),
    tenancy_commencement_date: Yup.date().required('Tenancy commencement date is required'),
    tenancy_expiration_date: Yup.date().required('Tenancy expiration date is required'),
    property_rent_start_date: Yup.date().optional(),
    property_rent_end_date: Yup.date().optional(),
  })

  const handleSubmit = () => {
    formik.setTouched(
      {
        property_sub_unit_uuid: true,
        property_rent_rate: true,
        business_name: true,
        business_email_id: true,
        business_contact_number: true,
        property_rent_due_date: true,
        security_deposit: true,
        utilities_deposit: true,
        other_deposits: true,
        rental_agreement_date: true,
        tenancy_term: true,
        tenancy_commencement_date: true,
        tenancy_expiration_date: true,
        property_rent_start_date: true,
        property_rent_end_date: true,
      },
      true
    )
    formik.validateForm().then(async (error) => {
      if (Object.keys(error).length === 0) {
        setEditTenantLoading(true)
        const {data: res} = await Edit_Tenant(
          id,
          formik.values.property_sub_unit_uuid,
          parseInt(formik.values.property_rent_rate, 10),
          formik.values.business_name,
          formik.values.business_email_id,
          formik.values.business_contact_number,
          formik.values.property_rent_due_date,
          formik.values.security_deposit ? parseInt(formik.values.security_deposit, 10): 0,
          parseInt(formik.values.utilities_deposit, 10),
          parseInt(formik.values.other_deposits, 10),
          formik.values.rental_agreement_date,
          parseInt(formik.values.tenancy_term, 10),
          formik.values.tenancy_commencement_date,
          formik.values.tenancy_expiration_date,
          formik.values.property_rent_start_date,
          formik.values.property_rent_end_date
        )
        if (res.code === '200') {
          setEditTenantLoading(false)
          Swal.fire({
            position: 'center',
            icon: 'success',
            title: res.msg.charAt(0).toUpperCase() + res.msg.slice(1),
            text: res.msg_details,
            allowOutsideClick: true,
          })
          get_view_tenant()
          setShow(false)
        } else {
          setEditTenantLoading(false)
          Swal.fire({
            position: 'center',
            icon: 'error',
            title: res.msg.charAt(0).toUpperCase() + res.msg.slice(1),
            text: res.msg_details,
            allowOutsideClick: true,
          })
        }
      } else {
        const firstErrorField = Object.keys(error)[0]
        if (firstErrorField) {
          // @ts-ignore
          const fieldElement = formRef.current?.elements[firstErrorField] as HTMLElement | undefined
          if (fieldElement) {
            fieldElement.scrollIntoView({behavior: 'smooth'})
          }
        }
      }
    })
  }
  const formik = useFormik({
    initialValues: tenantDetails,
    enableReinitialize: true,
    validationSchema,
    onSubmit: () => console.log(''),
  })
  const dueDates = Array.from({length: 31}, (_, index) => index + 1)
  const FormatDate = (date: any) => {
    const formattedDate = date.toLocaleDateString('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    return formattedDate
  }
  return (
    <>
      {!show && tenantDetails && (
        <div className='card mb-6 mb-xl-10' id='kt_profile_details_view'>
          <div className='card-header cursor-pointer'>
            <div className='card-title m-0'>
              <h3 className='fw-bold m-0'>Details</h3>
            </div>

            <button
              className='align-self-center btn btn-sm btn-primary me-3'
              onClick={() => setShow(true)}
            >
              Edit Tenant
            </button>
          </div>
          {editTenantLoading ? (
            <div
              className='d-flex justify-content-center align-items-center vh-70'
              style={{height: '70vh'}}
            >
              <CircularProgress />
            </div>
          ) : (
            <div className='card-body p-9 row'>
              <div className='col-md-6'>
                {/* {Begin :: Tenant Name} */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Tenant Name</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>
                      {tenantDetails.accouont_holder_name.charAt(0).toUpperCase() +
                        tenantDetails?.accouont_holder_name.slice(1)}
                    </span>
                  </div>
                </div>
                {/* {End :: Tenant Name} */}

                {/* {Begin :: Tenant Email Id} */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Tenant Email</label>
                  <div className='col-lg-6 d-flex align-items-center'>
                    <span className='fw-bold fs-6 pb-1 me-2'>{tenantDetails.email_id}</span>
                  </div>
                </div>
                {/* {End :: Tenant Email Id} */}

                {/* {Begin :: Tenant Contact Number} */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Tenant Contact Number</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>{tenantDetails.contact_number}</span>
                  </div>
                </div>
                {/* {End :: Tenant Contact Number} */}

                {/* {Begin :: Property Name} */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Property Name</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1 text-dark pb-1'>
                      {tenantDetails.property_title}
                    </span>
                  </div>
                </div>
                {/* {End :: Property Name} */}

                {/* {Begin :: Subdivisions} */}
                {tenantDetails.property_sub_unit_uuid && tenantDetails.sub_unit_available && (
                  <div className='row mb-6'>
                    <label className='col-lg-6 fw-bold text-muted'>Subdivisions</label>
                    <div className='col-lg-6'>
                      <span className='fw-bold fs-6 pb-1 text-dark pb-1'>
                        {tenantDetails.property_unit_name}
                      </span>
                    </div>
                  </div>
                )}
                {/* {End :: Subdivisions} */}

                {/* {Begin :: Rent Amount} */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Rent Amount</label>
                  <div className='col-lg-6 fv-row'>
                    <span className='fw-bold fs-6 pb-1'>
                      {`${
                        tenantDetails?.currency_code === 'INR' ? '₹' : tenantDetails?.currency_code
                      } ${tenantDetails?.property_rent_rate}`}
                    </span>
                  </div>
                </div>
                {/* {End :: Rent Amount} */}

                {/* {Begin :: Security Deposit} */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Security Deposit</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>
                      {`${
                        tenantDetails?.currency_code === 'INR' ? '₹' : tenantDetails?.currency_code
                      } ${tenantDetails?.security_deposit}`}
                    </span>
                  </div>
                </div>
                {/* {End :: Security Deposit} */}

                {/* {Begin :: Utilities Deposit} */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Utilities Deposit</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>
                      {tenantDetails.utilities_deposit
                        ? `${
                            tenantDetails?.currency_code === 'INR'
                              ? '₹'
                              : tenantDetails?.currency_code
                          } ${tenantDetails?.utilities_deposit}`
                        : '-'}
                    </span>
                  </div>
                </div>
                {/* {End :: Utilities Deposit} */}

                {/* {Begin :: Other Deposits} */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Other Deposits</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1 text-dark pb-1'>
                      {tenantDetails?.other_deposits
                        ? `${
                            tenantDetails?.currency_code === 'INR'
                              ? '₹'
                              : tenantDetails?.currency_code
                          } ${tenantDetails?.other_deposits}`
                        : '-'}
                    </span>
                  </div>
                </div>
                {/* {End :: Other Deposits} */}

                {/* {Begin :: Tenancy Term} */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Tenancy Term</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>
                      {tenantDetails.tenancy_term ? `${tenantDetails.tenancy_term} Months` : '-'}
                    </span>
                  </div>
                </div>
                {/* {End :: Tenancy Term} */}
              </div>

              <div className='col-md-6'>
                {/* {Begin:: Business details} */}
                {tenantDetails.property_usage_type === 2 && (
                  <>
                    {/* {Begin :: Business Name} */}
                    <div className='row mb-6'>
                      <label className='col-lg-6 fw-bold text-muted'>Business Name</label>
                      <div className='col-lg-6'>
                        <span className='fw-bold fs-6 pb-1 text-dark pb-1'>
                          {tenantDetails.business_name}
                        </span>
                      </div>
                    </div>
                    {/* {End :: Business Name} */}

                    {/* {Begin :: Business Email} */}
                    <div className='row mb-6'>
                      <label className='col-lg-6 fw-bold text-muted'>Business Email</label>
                      <div className='col-lg-6'>
                        <span className='fw-bold fs-6 pb-1'>{tenantDetails.business_email_id}</span>
                      </div>
                    </div>
                    {/* {End :: Business Email} */}

                    {/* {Begin :: Business Contact Number} */}
                    <div className='row mb-6'>
                      <label className='col-lg-6 fw-bold text-muted'>Business Contact Number</label>
                      <div className='col-lg-6'>
                        <span className='fw-bold fs-6 pb-1 text-dark pb-1'>
                          {tenantDetails.business_contact_number}
                        </span>
                      </div>
                    </div>
                    {/* {End :: Business Contact Number} */}
                  </>
                )}
                {/* {End:: Business details} */}

                {/* {Begin :: Rent Due Date} */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Rent Due Date</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>
                      <span>{`By ${tenantDetails?.property_rent_due_date}`}</span>
                      <sup>{dateSuperScript(tenantDetails?.property_rent_due_date)}</sup>
                      <span>{` of each month`}</span>
                    </span>
                  </div>
                </div>
                {/* {End :: Rent Due Date} */}

                {/* {Begin :: Agreement Date} */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Agreement Date</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>
                      {FormatDate(new Date(tenantDetails.rental_agreement_date))}
                    </span>
                  </div>
                </div>
                {/* {End :: Agreement Date} */}

                {/* {Begin :: Commencement Date} */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Commencement Date</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>
                      {FormatDate(new Date(tenantDetails.tenancy_commencement_date))}
                    </span>
                  </div>
                </div>
                {/* {End :: Commencement Date} */}

                {/* {Begin :: Expiration Date} */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Expiration Date</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>
                      {FormatDate(new Date(tenantDetails.tenancy_expiration_date))}
                    </span>
                  </div>
                </div>
                {/* {End :: Expiration Date} */}

                {/* {Begin :: Rent Start Date} */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Rent Start Date</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>
                      {tenantDetails.property_rent_start_date !== ''
                        ? FormatDate(new Date(tenantDetails.property_rent_start_date))
                        : '-'}
                    </span>
                  </div>
                </div>
                {/* {End :: Rent Start Date} */}

                {/* {Begin :: Rent End Date} */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Rent End Date</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>
                      {tenantDetails.property_rent_start_date !== ''
                        ? FormatDate(new Date(tenantDetails.property_rent_end_date))
                        : '-'}
                    </span>
                  </div>
                </div>
                {/* {End :: Rent End Date} */}
              </div>
            </div>
          )}
        </div>
      )}

      {show && (
        <div className='card mb-5 mb-xl-10'>
          <div className='card-header border-0 d-flex justify-content-between align-items-center'>
            <div className='card-title m-0'>
              <h3 className='fw-bolder m-0'>Details</h3>
            </div>
            <div>
              <button
                className='btn btn-sm btn-light fw-bold bg-color-white shadow me-4 btn-active-light-primary'
                onClick={() => setShow(false)}
              >
                Cancel
              </button>
              <button
                type='submit'
                className='btn btn-sm btn-primary shadow'
                onClick={handleSubmit}
                disabled={editTenantLoading}
              >
                {editTenantLoading ? (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    Processing...
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </div>

          <div id='kt_account_profile_details' className='collapse show'>
            <form ref={formRef} onSubmit={formik.handleSubmit} className='form'>
              <div className='card-body border-top p-9'>
                {/* {Begin:: form field business field} */}
                {tenantDetails.property_usage_type === 2 && (
                  <>
                    {/* {Begin:: form field Business Name} */}
                    <div className='row'>
                      <label className='col-lg-4 col-form-label fw-bold fs-6'>
                        <span className='required'>Business Name</span>
                        <i
                          className='fas fa-exclamation-circle ms-2 fs-7'
                          data-bs-toggle='tooltip'
                          title='Enter business name'
                        ></i>
                      </label>
                      <div className='col-lg-8 fv-row'>
                        <input
                          type='text'
                          className='form-control form-control-lg form-control-solid'
                          placeholder='Enter business name'
                          {...formik.getFieldProps('business_name')}
                          name='business_name'
                        />
                        <div className='h-20px'>
                          {formik.touched.business_name && formik.errors.business_name && (
                            <div className='fv-plugins-message-container mt-0'>
                              <div className='fv-help-block'>
                                {formik.errors.business_name.toString()}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* {End:: form field Business Name} */}

                    {/* {Begin:: form field Business Email} */}
                    <div className='row'>
                      <label className='col-lg-4 col-form-label fw-bold fs-6'>
                        <span className='required'>Business Email</span>
                        <i
                          className='fas fa-exclamation-circle ms-2 fs-7'
                          data-bs-toggle='tooltip'
                          title='Enter business email'
                        ></i>
                      </label>
                      <div className='col-lg-8 fv-row'>
                        <input
                          type='text'
                          className='form-control form-control-lg form-control-solid'
                          placeholder='Enter business email'
                          {...formik.getFieldProps('business_email_id')}
                        />
                        <div className='h-20px'>
                          {formik.touched.business_email_id && formik.errors.business_email_id && (
                            <div className='fv-plugins-message-container mt-0'>
                              <div className='fv-help-block'>
                                {formik.errors.business_email_id.toString()}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* {End:: form field Business Email} */}

                    {/* {Begin:: form field Business Contact Number} */}
                    <div className='row'>
                      <label className='col-lg-4 col-form-label fw-bold fs-6'>
                        <span className='required'>Business Contact Number</span>
                        <i
                          className='fas fa-exclamation-circle ms-2 fs-7'
                          data-bs-toggle='tooltip'
                          title='Enter business contact number'
                        ></i>
                      </label>

                      <div className='col-lg-8 fv-row'>
                        <MuiPhoneNumber
                          {...formik.getFieldProps('business_contact_number')}
                          className='w-100'
                          defaultCountry={'in'}
                          onChange={(value) =>
                            formik.setFieldValue('business_contact_number', value)
                          }
                        />
                        <div className='h-20px'>
                          {formik.touched.business_contact_number &&
                            formik.errors.business_contact_number && (
                              <div className='fv-plugins-message-container mt-0'>
                                <div className='fv-help-block'>
                                  {formik.errors.business_contact_number.toString()}
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                    {/* {End:: form field Business Contact Number} */}
                  </>
                )}
                {/* {End:: form field business field} */}

                {/* {Begin:: form field Subdivisions} */}
                {tenantDetails.sub_unit_available && subDivisions.length > 0 && (
                  <div className='row'>
                    <label className='col-lg-4 col-form-label fw-bold fs-6'>
                      <span className='required'>Subdivisions</span>
                      <i
                        className='fas fa-exclamation-circle ms-2 fs-7'
                        data-bs-toggle='tooltip'
                        title='Select property subdivision name'
                      ></i>
                    </label>

                    <div className='col-lg-8 fv-row'>
                      <select
                        className='form-select form-select-solid form-select-lg fw-bold'
                        {...formik.getFieldProps('property_sub_unit_uuid')}
                      >
                        <option value=''>Select property subdivision</option>
                        {subDivisions?.map((item: any) => {
                          return (
                            <option
                              key={item.property_sub_unit_uuid}
                              value={item.property_sub_unit_uuid}
                            >
                              {item.property_unit_name}
                            </option>
                          )
                        })}
                      </select>
                      <div className='h-20px'>
                        {formik.touched.property_sub_unit_uuid &&
                          formik.errors.property_sub_unit_uuid && (
                            <div className='fv-plugins-message-container mt-0'>
                              <div className='fv-help-block'>
                                {formik.errors.property_sub_unit_uuid.toString()}
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                )}
                {/* {End:: form field Subdivisions} */}

                {/* {Begin:: form field Rent Amount} */}
                <div className='row'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Rent Amount</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Enter rent amount'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Enter rent amount'
                      {...formik.getFieldProps('property_rent_rate')}
                    />
                    <div className='h-20px'>
                      {formik.touched.property_rent_rate && formik.errors.property_rent_rate && (
                        <div className='fv-plugins-message-container mt-0'>
                          <div className='fv-help-block'>
                            {formik.errors.property_rent_rate.toString()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* {End:: form field Rent Amount} */}

                {/* {Begin:: form field Security Deposit} */}
                <div className='row'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className=''>Security Deposit</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Enter security deposit'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Enter security deposit'
                      {...formik.getFieldProps('security_deposit')}
                    />
                    <div className='h-20px'>
                      {formik.touched.security_deposit && formik.errors.security_deposit && (
                        <div className='fv-plugins-message-container mt-0'>
                          <div className='fv-help-block'>
                            {formik.errors.security_deposit.toString()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* {End:: form field Security Deposit} */}

                {/* {Begin:: form field Utilities Deposit} */}
                <div className='row'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className=''>Utilities Deposit</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Enter utilities deposit'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Enter utilities deposit'
                      {...formik.getFieldProps('utilities_deposit')}
                    />
                    <div className='h-20px'>
                      {formik.touched.utilities_deposit && formik.errors.utilities_deposit && (
                        <div className='fv-plugins-message-container mt-0'>
                          <div className='fv-help-block'>
                            {formik.errors.utilities_deposit.toString()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* {End:: form field Utilities Deposit} */}

                {/* {Begin:: form field Other Deposits} */}
                <div className='row'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className=''>Other Deposits</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Enter other deposits'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Enter other deposits'
                      {...formik.getFieldProps('other_deposits')}
                    />
                    <div className='h-20px'>
                      {formik.touched.other_deposits && formik.errors.other_deposits && (
                        <div className='fv-plugins-message-container mt-0'>
                          <div className='fv-help-block'>
                            {formik.errors.other_deposits.toString()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* {End:: form field Other Deposits} */}

                {/* {Begin:: form field Rent Due Date} */}
                <div className='row'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Rent Due Date</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Enter rent due date'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <select
                      className='form-select form-select-solid d-flex'
                      {...formik.getFieldProps('property_rent_due_date')}
                      name='property_rent_due_date'
                      placeholder='Select due date'
                    >
                      <option className='bg-color-gray' value=''>
                        Select due date
                      </option>
                      {dueDates.map((item: any) => {
                        return (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        )
                      })}
                    </select>
                    <div className='h-20px'>
                      {formik.touched.property_rent_due_date &&
                        formik.errors.property_rent_due_date && (
                          <div className='fv-plugins-message-container mt-0'>
                            <div className='fv-help-block'>
                              {formik.errors.property_rent_due_date.toString()}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
                {/* {End:: form field Rent Due Date} */}

                {/* {Begin:: form field Tenancy Term} */}
                <div className='row'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className=''>Tenancy Term</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Enter tenancy term'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row position-relative'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid right-padding'
                      placeholder='Enter tenancy term'
                      {...formik.getFieldProps('tenancy_term')}
                    />
                    <div
                      className='position-absolute end-0 px-7 top-0 month-padding fs-5'
                      style={{color: '#B5B5C3', fontWeight: '600'}}
                    >
                      Months
                    </div>
                    <div className='h-20px'>
                      {formik.touched.tenancy_term && formik.errors.tenancy_term && (
                        <div className='fv-plugins-message-container mt-0'>
                          <div className='fv-help-block'>
                            {formik.errors.tenancy_term.toString()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* {End:: form field Tenancy Term} */}

                {/* {Begin:: form field Agreement Date} */}
                <div className='row'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Agreement Date</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Enter rental agreement date'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <DatePicker
                      autoComplete='off'
                      selected={agreementDate}
                      dateFormat='yyyy/MM/dd'
                      placeholderText='Select rental agreement date'
                      onChange={(date: any) => {
                        formik.setFieldTouched('rental_agreement_date', true, true)
                        if (date === null) {
                          setAgreementDate(null)
                          formik.setFieldValue('rental_agreement_date', '')
                        } else {
                          setAgreementDate(date)
                          formik.setFieldValue('rental_agreement_date', FormatDate(date))
                        }
                      }}
                      className='form-control form-control-lg form-control-solid'
                    />
                    <div className='h-20px'>
                      {formik.touched.rental_agreement_date && formik.errors.rental_agreement_date && (
                        <div className='fv-plugins-message-container mt-0'>
                          <div className='fv-help-block'>
                            {formik.errors.rental_agreement_date.toString()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* {End:: form field Agreement Date} */}

                {/* {Begin:: form field Commencement Date} */}
                <div className='row'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Commencement Date</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Enter tenancy commencement date'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <DatePicker
                      autoComplete='off'
                      selected={tenancyStartDate}
                      dateFormat='yyyy/MM/dd'
                      placeholderText='Select tenancy commencement date'
                      selectsStart
                      startDate={tenancyStartDate}
                      endDate={tenancyEndDate}
                      maxDate={tenancyEndDate}
                      onChange={(date: any) => {
                        formik.setFieldTouched('tenancy_commencement_date', true, true)
                        if (date === null) {
                          setTenancyStartDate(null)
                          formik.setFieldValue('tenancy_commencement_date', '')
                        } else {
                          setTenancyStartDate(date)
                          formik.setFieldValue('tenancy_commencement_date', FormatDate(date))
                        }
                      }}
                      className='form-control form-control-lg form-control-solid'
                    />
                    <div className='h-20px'>
                      {formik.touched.tenancy_commencement_date &&
                        formik.errors.tenancy_commencement_date && (
                          <div className='fv-plugins-message-container mt-0'>
                            <div className='fv-help-block'>
                              {formik.errors.tenancy_commencement_date.toString()}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
                {/* {End:: form field Commencement Date} */}

                {/* {Begin:: form field Expiration Date} */}
                <div className='row'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Expiration Date</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Enter tenancy expiration date'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <DatePicker
                      autoComplete='off'
                      selected={tenancyEndDate}
                      dateFormat='yyyy/MM/dd'
                      placeholderText='Enter tenancy expiration date'
                      selectsEnd
                      startDate={tenancyStartDate}
                      endDate={tenancyEndDate}
                      minDate={tenancyStartDate}
                      onChange={(date: any) => {
                        formik.setFieldTouched('tenancy_expiration_date', true, true)
                        if (date === null) {
                          setTenancyEndDate(null)
                          formik.setFieldValue('tenancy_expiration_date', '')
                        } else {
                          setTenancyEndDate(date)
                          formik.setFieldValue('tenancy_expiration_date', FormatDate(date))
                        }
                      }}
                      className='form-control form-control-lg form-control-solid'
                    />
                    <div className='h-20px'>
                      {formik.touched.tenancy_expiration_date &&
                        formik.errors.tenancy_expiration_date && (
                          <div className='fv-plugins-message-container mt-0'>
                            <div className='fv-help-block'>
                              {formik.errors.tenancy_expiration_date.toString()}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
                {/* {End:: form field Expiration Date} */}

                {/* {Begin:: form field Rent Start Date} */}
                <div className='row'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className=''>Rent Start Date</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Enter property rent start date'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <DatePicker
                      autoComplete='off'
                      selected={rentalStartDate}
                      dateFormat='yyyy/MM/dd'
                      placeholderText='Select property rent start date'
                      selectsStart
                      startDate={rentalStartDate}
                      endDate={rentalEndDate}
                      maxDate={rentalEndDate}
                      onChange={(date: any) => {
                        formik.setFieldTouched('property_rent_start_date', true, true)
                        if (date === null) {
                          setRentalStartDate(null)
                          formik.setFieldValue('property_rent_start_date', '')
                        } else {
                          setRentalStartDate(date)
                          formik.setFieldValue('property_rent_start_date', FormatDate(date))
                        }
                      }}
                      className='form-control form-control-lg form-control-solid'
                    />
                    <div className='h-20px'>
                      {formik.touched.property_rent_start_date &&
                        formik.errors.property_rent_start_date && (
                          <div className='fv-plugins-message-container mt-0'>
                            <div className='fv-help-block'>
                              {formik.errors.property_rent_start_date.toString()}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
                {/* {End:: form field Rent Start Date} */}

                {/* {Begin:: form field Rent End Date} */}
                <div className='row'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className=''>Rent End Date</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Enter property rent end date'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <DatePicker
                      autoComplete='off'
                      selected={rentalEndDate}
                      dateFormat='yyyy/MM/dd'
                      placeholderText='Select property rent end date'
                      selectsEnd
                      startDate={rentalStartDate}
                      endDate={rentalEndDate}
                      minDate={rentalStartDate}
                      onChange={(date: any) => {
                        formik.setFieldTouched('property_rent_end_date', true, true)
                        if (date === null) {
                          setRentalEndDate(null)
                          formik.setFieldValue('property_rent_end_date', '')
                        } else {
                          setRentalEndDate(date)
                          formik.setFieldValue('property_rent_end_date', FormatDate(date))
                        }
                      }}
                      className='form-control form-control-lg form-control-solid'
                    />
                    <div className='h-20px'>
                      {formik.touched.property_rent_end_date &&
                        formik.errors.property_rent_end_date && (
                          <div className='fv-plugins-message-container mt-0'>
                            <div className='fv-help-block'>
                              {formik.errors.property_rent_end_date.toString()}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
                {/* {End:: form field Rent End Date} */}

                <div className='card-footer d-flex justify-content-end py-6 px-3'>
                  <button
                    className='btn btn-sm btn-light fw-bold bg-color-white shadow me-4 btn-active-light-primary'
                    onClick={() => setShow(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type='submit'
                    className={`btn btn-sm btn-primary shadow`}
                    onClick={handleSubmit}
                    disabled={editTenantLoading}
                  >
                    {editTenantLoading ? (
                      <span className='indicator-progress' style={{display: 'block'}}>
                        Processing...
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    ) : (
                      'Save'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}

export default TenantOverview
